import type { GameState, Phase } from './physics';
import type { GoalTag, PhysicsEvent, Role } from '../types';
import { randomBotQuip, randomQuip } from './quips';

/**
 * HTML overlay HUD: score number at top-center and phase banner in the
 * middle. Lives outside the canvas so it works identically for the
 * Canvas-2D and WebGL renderers. Animations are pure CSS (transition +
 * keyframes); this module only flips classes and writes text.
 */
export interface Hud {
  tick(state: GameState, now: number): void;
  applyEvents(events: readonly PhysicsEvent[]): void;
  destroy(): void;
}

const BUMP_MS = 450;

export function createHud(container: HTMLElement, role: Role, vsBot = false): Hud {
  const root = document.createElement('div');
  root.className = 'hud';

  const score = document.createElement('div');
  score.className = 'hud-score';

  // Color the digits to match each player's paddle. The "you" digit
  // (right) is always the local player's role color. CSS uses
  // var(--host) / var(--guest) so the active palette drives the hue.
  const youColorClass = role === 'host' ? 'hud-score-host' : 'hud-score-guest';
  const oppColorClass = role === 'host' ? 'hud-score-guest' : 'hud-score-host';
  const oppDigit = document.createElement('span');
  oppDigit.className = `hud-score-digit ${oppColorClass}`;
  const sep = document.createElement('span');
  sep.className = 'hud-score-sep';
  sep.textContent = ':';
  const youDigit = document.createElement('span');
  youDigit.className = `hud-score-digit ${youColorClass}`;

  score.appendChild(oppDigit);
  score.appendChild(sep);
  score.appendChild(youDigit);

  const banner = document.createElement('div');
  banner.className = 'hud-banner';

  // Subtitle line under the banner — used for spectator quips on flair goals.
  const quipLine = document.createElement('div');
  quipLine.className = 'hud-banner-quip';

  // Full-stage tint flash on goals. Color set per-goal from scorer side.
  const flash = document.createElement('div');
  flash.className = 'hud-flash';

  root.appendChild(score);
  root.appendChild(banner);
  root.appendChild(quipLine);
  root.appendChild(flash);
  container.appendChild(root);

  // role determines which side's score gets which color and label.
  const youIsHost = role === 'host';

  let lastHostScore = -1;
  let lastGuestScore = -1;
  let bumpHostUntil = 0;
  let bumpGuestUntil = 0;

  let lastPhase: Phase | null = null;
  let lastPaused = false;
  // Captured from the most recent goal event so the banner can show
  // flair on the upcoming phase transition. Reset after consumed.
  let pendingGoalTag: GoalTag | undefined;
  let pendingGoalScorer: 'host' | 'guest' | undefined;

  // Quip chosen at goal time and shown when the phase transitions to a
  // goal-X — held until the next 'play' phase clears it.
  let pendingQuip: string | null = null;
  // Scorer color sampled when the goal event fires; consumed when the
  // banner enters on phase transition to tint the screen flash.
  let pendingFlashColor: string | null = null;

  const applyEvents = (events: readonly PhysicsEvent[]): void => {
    for (const ev of events) {
      if (ev.e === 'goal') {
        pendingGoalTag = ev.tag;
        pendingGoalScorer = ev.scorer;
        // Quip selection:
        //   - vsBot, untagged goal → always bot quip (otherwise silent).
        //   - vsBot, tagged goal   → 50/50 tag quip vs bot quip.
        //   - multiplayer, tagged  → tag quip (existing behavior).
        //   - multiplayer, untagged → no quip.
        const tagQuip = randomQuip(ev.tag);
        if (vsBot) {
          const youScored =
            (ev.scorer === 'host' && role === 'host') ||
            (ev.scorer === 'guest' && role === 'guest');
          if (!tagQuip || Math.random() < 0.5) {
            pendingQuip = randomBotQuip(youScored);
          } else {
            pendingQuip = tagQuip;
          }
        } else {
          pendingQuip = tagQuip;
        }
        // Capture scorer color for the screen flash (read on phase
        // transition into goal-X below).
        pendingFlashColor = ev.scorer === 'host'
          ? getComputedStyle(document.documentElement).getPropertyValue('--host').trim()
          : getComputedStyle(document.documentElement).getPropertyValue('--guest').trim();
      }
    }
  };

  const tick = (state: GameState, now: number): void => {
    // Score updates + bump animation.
    if (state.score.host !== lastHostScore) {
      if (lastHostScore !== -1 && state.score.host > lastHostScore) {
        bumpHostUntil = now + BUMP_MS;
      }
      lastHostScore = state.score.host;
      oppDigit.textContent = String(youIsHost ? state.score.guest : state.score.host);
      youDigit.textContent = String(youIsHost ? state.score.host : state.score.guest);
    }
    if (state.score.guest !== lastGuestScore) {
      if (lastGuestScore !== -1 && state.score.guest > lastGuestScore) {
        bumpGuestUntil = now + BUMP_MS;
      }
      lastGuestScore = state.score.guest;
      oppDigit.textContent = String(youIsHost ? state.score.guest : state.score.host);
      youDigit.textContent = String(youIsHost ? state.score.host : state.score.guest);
    }

    const youBumping = youIsHost ? bumpHostUntil > now : bumpGuestUntil > now;
    const oppBumping = youIsHost ? bumpGuestUntil > now : bumpHostUntil > now;
    youDigit.classList.toggle('bump', youBumping);
    oppDigit.classList.toggle('bump', oppBumping);

    // Pause overrides the phase banner. When paused, show a paused
    // overlay; on unpause, fall through to normal phase handling.
    if (state.paused !== lastPaused) {
      lastPaused = state.paused;
      if (state.paused) {
        banner.classList.remove('flair', 'flair-own', 'flair-shutout', 'flair-hat-trick', 'flair-snipe', 'flair-comeback', 'flair-ace', 'flair-dunk', 'flair-speedrun', 'flair-long', 'flair-banger', 'flair-toilet', 'flair-top-shelf', 'flair-bank');
        banner.classList.add('flair-paused');
        banner.textContent = 'PAUSED';
        quipLine.classList.remove('show');
        banner.classList.remove('show');
        void banner.offsetWidth;
        banner.classList.add('show');
        return;
      }
      banner.classList.remove('flair-paused');
      // Re-apply phase banner if we're not in play.
      if (state.phase !== 'play') {
        const msg = bannerMessage(state.phase, youIsHost, pendingGoalScorer, pendingGoalTag);
        banner.textContent = msg;
        if (pendingGoalTag) banner.classList.add('flair', `flair-${pendingGoalTag}`);
        banner.classList.remove('show');
        void banner.offsetWidth;
        banner.classList.add('show');
      } else {
        banner.classList.remove('show');
      }
      // Phase-change handling below skipped this frame because we just
      // rewrote the banner — early-return.
      lastPhase = state.phase;
      return;
    }
    if (state.paused) return;

    // Phase banner.
    if (state.phase !== lastPhase) {
      lastPhase = state.phase;
      // Reset any flair class state.
      banner.classList.remove('flair', 'flair-own', 'flair-shutout', 'flair-hat-trick', 'flair-snipe', 'flair-comeback', 'flair-ace', 'flair-dunk', 'flair-speedrun', 'flair-long', 'flair-banger', 'flair-toilet', 'flair-top-shelf', 'flair-bank');
      if (state.phase === 'play') {
        banner.classList.remove('show');
        quipLine.classList.remove('show');
        pendingGoalTag = undefined;
        pendingGoalScorer = undefined;
        pendingQuip = null;
      } else {
        const msg = bannerMessage(state.phase, youIsHost, pendingGoalScorer, pendingGoalTag);
        banner.textContent = msg;
        if (pendingGoalTag) {
          banner.classList.add('flair', `flair-${pendingGoalTag}`);
        }
        // Force reflow so the entrance animation replays even when the
        // class was already present (back-to-back non-play phases).
        banner.classList.remove('show');
        void banner.offsetWidth;
        banner.classList.add('show');

        if (pendingQuip) {
          quipLine.textContent = pendingQuip;
          quipLine.classList.remove('show');
          void quipLine.offsetWidth;
          quipLine.classList.add('show');
        } else {
          quipLine.classList.remove('show');
        }
        // Screen flash on goal phases only (skip serve/get-ready).
        if (pendingFlashColor && (state.phase === 'goal-host' || state.phase === 'goal-guest')) {
          flash.style.background = pendingFlashColor;
          flash.classList.remove('flash');
          void flash.offsetWidth;
          flash.classList.add('flash');
        }
      }
    }
  };

  const destroy = (): void => {
    root.remove();
  };

  return { tick, applyEvents, destroy };
}

function bannerMessage(
  phase: Phase,
  youIsHost: boolean,
  scorer: 'host' | 'guest' | undefined,
  tag: GoalTag | undefined,
): string {
  if (phase === 'serve') return 'GET READY';
  const youScored = (phase === 'goal-host' && youIsHost) || (phase === 'goal-guest' && !youIsHost);
  // Special tags: bespoke text; no need to also say "you score" since
  // the puck-explosion color already cues who scored.
  if (tag === 'own') return 'OWN GOAL';
  if (tag === 'shutout') return 'SHUTOUT';
  if (tag === 'hat-trick') return 'HAT TRICK';
  if (tag === 'snipe') return youScored ? 'SNIPE!' : 'SNIPED';
  if (tag === 'comeback') return youScored ? 'COMEBACK!' : 'COMEBACK';
  if (tag === 'ace') return youScored ? 'ACE!' : 'ACED';
  if (tag === 'dunk') return youScored ? 'DUNK!' : 'DUNKED ON';
  if (tag === 'speedrun') return 'SPEEDRUN';
  if (tag === 'long') return youScored ? 'LONG SHOT!' : 'LONG SHOT';
  if (tag === 'banger') return youScored ? 'BANGER!' : 'BANGER';
  if (tag === 'toilet') return 'ROLLED IN';
  if (tag === 'top-shelf') return 'TOP SHELF';
  if (tag === 'bank') return youScored ? 'BANK SHOT!' : 'BANK SHOT';
  // Avoid unused warning while keeping scorer in the signature for future use.
  void scorer;
  return youScored ? 'YOU SCORE' : 'OPPONENT SCORES';
}
